/**
 * This is the driver for order submission.  Each topic is its own component that allows them
 * to easily be changed and order swapped around.
 */
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Grid, Paper, Typography } from '@mui/material';

import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';

import AbEnvironment from '../auth/AbEnvironment';

import StepReseller from './StepReseller';
import StepCustomer from './StepCustomer';
import StepApp from './StepApp';
import PoweredBy from "./PoweredBy";
import SubscriptionOrderCreateMutation from '../auth/SubscriptionOrderCreateMutation';


const abEnvironment = new AbEnvironment();


const styles = theme => ({
  
  stepperPaper: {
    borderTopRightRadius: "10px",
    borderTopLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    borderBottomLeftRadius: "10px",
    maxWidth: 600,
    minWidth: 466,
    width: "100%"

  },
  title: {
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: theme.palette.secondary.main,  
    borderTopRightRadius: "10px",
    borderTopLeftRadius: "10px"
  },
  titleLine: {
    color : "white", 
    fontSize: "21px",
    fontWeight: "500",
    paddingLeft: "16px",
    paddingTop: "24px",
    paddingBottom: "8px"
  },
  stepper: {
    borderBottomRightRadius: "10px",
    borderBottomLeftRadius: "10px",
    padding: "16px"
  },
});


class StepperSubscriptionOrder extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,

      dataLoaded: false,

      activeStep: 0,
      skipped: new Set(),

      displayOnly: false,

      reseller: {
        companyName:   '',
        contactLastName: '', 
        contactFirstName: '', 
        contactEmail: ''
      },
  
      customer: {
        companyName:   '',
        contactLastName: '', 
        contactFirstName: '', 
        contactEmail: ''
      },

      app: {
        appsSelected: [],
      },

      whopay: {
        choice: ''
      },

      terms: {
        markup: '0.00',
        passFees: '1',
        showCustomer: '1',
        wholesaleDiscountPercentage: '0.00',

      }

    };

  }

  componentDidMount() {
    
    //if json is passed it means this component is being used before subscription exists
    //this happens on signup / confirm email pages.
    if (this.props.json) {
      let json = this.props.json;
      this.setState({dataLoaded: true, displayOnly: true, reseller: json.reseller, customer: json.customer, app: json.app, whopay: json.whopay, terms: json.terms, activeStep: 5});
    } 
    //if a subscription was passed it means it is already existing, 
    //display it in display only mode. It does this by setting state
    //and passing display only to all components that participate.
    else if (this.props.customerApp) {

      //this is setting all the state that the subscription order stepper understands.
      let reseller = {
        companyName:   this.props.customerApp.accountTByCustomerAccountId.accountTByParentId.accountName,
        contactLastName: this.props.customerApp.accountTByCustomerAccountId.accountTByParentId.contactLastName,  
        contactFirstName: this.props.customerApp.accountTByCustomerAccountId.accountTByParentId.contactFirstName,
        contactEmail: this.props.customerApp.accountTByCustomerAccountId.accountTByParentId.contactEmail
      };
  
      let customer =  {
        companyName:   this.props.customerApp.accountTByCustomerAccountId.accountName,
        contactLastName: this.props.customerApp.accountTByCustomerAccountId.contactLastName,
        contactFirstName: this.props.customerApp.accountTByCustomerAccountId.contactFirstName, 
        contactEmail: this.props.customerApp.accountTByCustomerAccountId.contactEmail
      };

      let appSelected = {
        appName: this.props.customerApp.appTByAppId.appName,
        appSku: this.props.customerApp.appTByAppId.appSku,
        appLogo: this.props.customerApp.appTByAppId.appLogo,
        description:  this.props.customerApp.appTByAppId.description,
      }
      let app = {
        appsSelected: [appSelected]
      }

      //TODO - what do we do about a 1 and 2?
      let whopay = {};
      if (this.props.customerApp.paymentOrder === 0 || this.props.customerApp.paymentOrder === 1) {
        whopay.choice = "0"
      } else {
        whopay.choice = "3"
      }

      let terms = {
        markup: this.props.customerApp.appMarkupPercentage,
        showCustomer: this.props.customerApp.showSubscription,
        passFees: this.props.customerApp.passThroughFees,
        wholesaleDiscountPercentage: this.props.customerApp.wholesaleDiscountPercentage
      }

      this.setState({dataLoaded: true, displayOnly: true, reseller: reseller, customer: customer, app: app, whopay: whopay, terms: terms, activeStep: 5});
    } else if (this.props.auth.isAuthenticatedReseller()) {

        //reseller already logged in
        let reseller = {
          companyName:   abEnvironment.getUserAttribute('a_accountName'),
          contactLastName: abEnvironment.getUserAttribute('a_lastName'),  
          contactFirstName: abEnvironment.getUserAttribute('a_firstName'),
          contactEmail: abEnvironment.getUserAttribute('a_email')
        };
        //this gets passed from parent.  In effect a landing page may be associated with a commission program.
        //if so, the conversion process will create that relationship.
        if (this.props.landingPath) {
          reseller['landingPath'] = this.props.landingPath;
        } else {
          reseller['landingPath'] = '';
        }
        if (this.props.soLandingId) {
          reseller['soLandingId'] = this.props.soLandingId;
        } else {
          reseller['soLandingId'] = '';
        }


        this.setState({dataLoaded: true, reseller: reseller, activeStep: 1});
  
    } else {
      this.setState({dataLoaded: true});
    }
    
  }


  getSteps = (activeStep) => {
    var steps = [];
    steps.push('Who are you?');
    steps.push('Who is your customer?');
    steps.push('What subscription are you purchasing?');
  
    return steps;
  }
  
  getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepReseller  auth={this.props.auth} 
                            reseller={this.state.reseller}
                            setResellerStateChanged={this.setResellerStateChanged}
                            assignedStep={0}
                            activeStep={this.state.activeStep}
                            displayOnly={this.state.displayOnly}
                            handleBack={this.handleBack}
                            handleNext={this.handleNext}/>
          );
      case 1:
        return (
          <StepCustomer auth={this.props.auth}
                            customer={this.state.customer}
                            setCustomerStateChanged={this.setCustomerStateChanged}
                            assignedStep={1}
                            activeStep={this.state.activeStep}
                            displayOnly={this.state.displayOnly}
                            handleBack={this.handleBack}
                            handleNext={this.handleNext}
                               />

        );
      case 2:
        return (
          <StepApp auth={this.props.auth}
                      appCount ={this.props.landingAppCount}
                      apps = {this.props.landingApps}
                      appsSelected = { this.state.app.appsSelected }
                      setAppStateChanged={this.setAppStateChanged}
                      assignedStep={2}
                      activeStep={this.state.activeStep}
                      displayOnly={this.state.displayOnly}
                      handleBack={this.handleBack}
                      handleNext={this.handlePlaceOrder}/>
        );
      default:
        return 'Unknown step';
    }
  }
  

  handleNext = () => {
    let newStep = this.state.activeStep;
    newStep++;
    this.setState({activeStep: newStep});
  }

  handleBack = () => {
    let newStep = this.state.activeStep;
    newStep--;
    this.setState({activeStep: newStep});
  }

  handlePlaceOrder = () => {
    const { logError } = this.props.auth;

    let newStep = this.state.activeStep;
    newStep++;
    this.setState({activeStep: newStep});
    /*console.log('reseller->' + JSON.stringify(this.state.reseller));
    console.log('customer->' + JSON.stringify(this.state.customer));
    console.log('apps->' + JSON.stringify(this.state.app));
    console.log('whopays->' + JSON.stringify(this.state.whopay));
    console.log('terms->' + JSON.stringify(this.state.terms));*/
    var signup = {};
    signup.reseller = this.state.reseller;
    signup.customer = this.state.customer;
    signup.app = this.state.app;
    signup.whopay = this.state.whopay;
    signup.terms = this.state.terms;
 
 
    if (!this.props.auth.isAuthenticatedReseller()) { 
      localStorage.setItem('subscriptionOrder', JSON.stringify(signup));
      this.props.history.replace(`/steppersignin`);
    } else {
      let thisPage = this;
      let acctId = abEnvironment.getUserAttribute('a_id');

      Promise.resolve()
      .then(() => {
        return this.saveSubscriptionOrderJSON(acctId, signup);
      })
      .then(resp => {    
        return this.convertSubscriptionOrder(thisPage);
      })
      .then((resp) => {
        if (resp && resp.data && resp.data.result  && resp.data.result.data) {
          
          //old version of proc returned customerAppId - we are no longer creating it here.  So, nothing to capture.
        }
      })
      .then(resp => {
        thisPage.props.history.replace('/subscriptionorder');  
        window.location.reload();  
      })
      .catch(err => {
        logError('Exception in StepperSubscriptionOrder.js: ' + err);
      });
  
    }
  }

  //used for prexisting resellers
  saveSubscriptionOrderJSON(accountId, soJSON) {
    return new Promise((resolve, reject) => {
      SubscriptionOrderCreateMutation.commit(accountId, soJSON)
      .then(socm => {
        localStorage.removeItem('subscriptionOrder');
        resolve(socm);
      })
      .catch(err => {
        reject(err);
      });
    });
  }  

  //used for prexisting resellers
  convertSubscriptionOrder = (thisPage) => {
    
    var userId = abEnvironment.getUserAttribute("u_id");
    let accountId = abEnvironment.getUserAttribute('a_id');
    const { callPartnerApi } = this.props.auth;

    return new Promise(function(resolve, reject) {
      let aPjwt = abEnvironment.getPjwt();

      // Call server side process JSON order.
      let payload = {
        operation: "convertSubscriptionOrder3",
        params: {
          pJwt:       aPjwt,
          pAccountId: accountId,
          userId:     userId
        }
      };
  
      return callPartnerApi('appbind/generic',
                            'POST',
                            payload)
    
      .then(response => {
        return response.json();
      })
      .then(data => {
        resolve(data);
      })
      .catch(err => {
        // console.log('err');
        reject(err);
      });
    });

  }

  setResellerStateChanged = (akey, avalue) => {
    let reseller = this.state.reseller;
    reseller[akey] = avalue;

    //this gets passed from parent.  In effect a landing page may be associated with a commission program.
    //if so, the conversion process will create that relationship.
    if (this.props.landingPath) {
      reseller['landingPath'] = this.props.landingPath;
    } else {
      reseller['landingPath'] = '';
    }
    if (this.props.soLandingId) {
      reseller['soLandingId'] = this.props.soLandingId;
    } else {
      reseller['soLandingId'] = '';
    }

    this.setState({reseller: reseller});
  }


  setCustomerStateChanged = (akey, avalue) => {
    let customer = this.state.customer;
    customer[akey] = avalue;
    this.setState({customer: customer});
  }

  setAppStateChanged = (appsSelected) => {
    let app = this.state.app;
    app.appsSelected = appsSelected;
    this.setState({app: app});
  }

  setWhoPayStateChanged = (choice) => {
    let whopay = this.state.whopay;
    whopay.choice = choice;
    this.setState({whopay: whopay});

    //if they are changing to a non-customer pay scenario, change the terms options
    //back to default.
    if (whopay.choice !== "0") {
      let initializedTerms= {markup: '0.00',passFees: '1', showCustomer: '1', wholesaleDiscountPercentage: '0.00' }
      this.setState({'terms': initializedTerms});
    }
  } 
 
  setTermsStateChanged = (akey, avalue) => {
    let terms = this.state.terms;
    terms[akey] = avalue;
    this.setState({terms: terms});
  }
 
  setPassFeesChanged = (akey, avalue) => {
    let terms = this.state.terms;
    terms[akey] = avalue;
    this.setState({terms: terms});
  }
  
  render() {
    //const { isAuthenticated, isAuthenticatedReseller, needAssent } = this.props.auth;
    const { classes } = this.props;

    return (
      <React.Fragment>

        {this.state.dataLoaded &&
        <Grid container justifyContent="center" spacing={2}>

          <Paper className={classes.stepperPaper}>
            <Grid item xs={12}>
              <div className={classes.title} style={this.state.displayOnly ? {backgroundColor:"#7d54ea"} : {}}>
                <Typography variant="h4" className={classes.titleLine} >
                  {this.state.displayOnly ? "Subscription order" : "Order subscription"}
                </Typography>
              </div>
            </Grid>
            
            {this.state.displayOnly &&
            <Grid item  xs={12} style={{marginBottom: 16, marginLeft: 16}}>
                {this.getSteps(this.state.activeStep).map((label, index) => (
                    <div style={{marginTop: 16}} key={index}>
                      {this.getStepContent(index)}
                    </div>
                 ))}
                

            </Grid>
            }

            {!this.state.displayOnly &&
            <Grid item  xs={12}>

              <Stepper activeStep={this.state.activeStep} orientation="vertical" className={classes.stepper}>
                {this.getSteps(this.state.activeStep).map((label, index) => (
                  <Step key={label} expanded={this.state.activeStep >= index} >
                    <StepLabel>{label}</StepLabel>
                    <StepContent>
                      {this.getStepContent(index)}
                  </StepContent>
                  </Step>
                ))}
              </Stepper>

            </Grid>
            }

          </Paper>
          
          {!this.state.displayOnly && <PoweredBy/>}

        </Grid>
        }

      </React.Fragment>
    );
  }
}

export default withStyles(styles)(StepperSubscriptionOrder);